<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Documentation/Interpreter_Framework';
    let nextLink= '/Documentation/Interpreter_Framework/Understanding_the_Framework';
    
</script>
<PrevNextSection {prevLink} {nextLink} />
<SectionComponent tag="h1" 
  id="the-expressions-dsl-1"
  bind:intersecting={visibleById["the-expressions-dsl-1"]}
  onIntersectChange={(visible) => setVisible("the-expressions-dsl-1", visible)}
>The Expressions DSL</SectionComponent>
<p>Before explaining the interpreter framework, we introduce a simple DSL that will serve as our running example. This DSL
will serve as the foundation for illustrating how Freon’s interpreter framework processes and evaluates expressions.
The language defines a single model unit, <code>ExpressionUnit</code>, which contains both function definitions and expressions.
You can explore this language by creating a new Freon project using <code>npm create freon</code>, then selecting the
<code>Expressions</code> template.</p>
<pre class="language-freon"><code class="language-freon">// Expressions/src/defs/Expressions.ast#L1-L11

language Expressions

model Expressions &#123;
    name: identifier;
    units: ExpressionUnit[];
&#125;

modelunit ExpressionUnit &#123;
    functions: Function[];
    expressions: Expression[];
&#125;</code></pre>
<SectionComponent tag="h2" 
  id="numeric-expressions-2"
  bind:intersecting={visibleById["numeric-expressions-2"]}
  onIntersectChange={(visible) => setVisible("numeric-expressions-2", visible)}
>Numeric Expressions</SectionComponent>
<p>We add several basic expression concepts to represent numeric
operations: <code>NumberLiteralExpression</code> and standard binary expressions.
Operator priorities are specified to support straightforward editing (See <a
  href="/Documentation/Defining_an_Editor/Binary_Expressions"
>Binary Expressions</a> for details.):</p>
<pre class="language-freon"><code class="language-freon">// Expressions/src/defs/Expressions.ast#L15-L40

abstract expression Expression &#123; &#125;

expression NumberLiteralExpression base Expression &#123;
    value: number;
&#125;

abstract binary expression BinaryExpression base Expression &#123;
    left: Expression;
    right: Expression;
&#125;

binary expression MultiplyExpression base BinaryExpression &#123;
    priority = 8;
&#125;

binary expression PlusExpression base BinaryExpression &#123;
    priority = 4;
&#125;

binary expression MinusExpression base BinaryExpression &#123;
    priority = 4;
&#125;

binary expression DivideExpression base BinaryExpression &#123;
    priority = 8;
&#125;</code></pre>
<SectionComponent tag="h2" 
  id="functions-3"
  bind:intersecting={visibleById["functions-3"]}
  onIntersectChange={(visible) => setVisible("functions-3", visible)}
>Functions</SectionComponent>
<p>We also define concepts to represent functions — both their definitions and calls:</p>
<pre class="language-freon"><code class="language-freon">// Expressions/src/defs/Expressions.ast#L42-L61

concept Function &#123;
    name: identifier;
    body: Expression;
    parameters: Parameter[];
    reference declaredType : Type;
&#125;

concept Parameter &#123;
    name: identifier;
    reference declaredType: Type;
&#125;

expression ParameterRef base Expression &#123;
    reference parameter: Parameter;
&#125;

expression FunctionCallExpression base Expression &#123;
    reference calledFunction: Function;
    arguments: Expression[];
&#125;</code></pre>


<PrevNextSection {prevLink} {nextLink} />